import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Input, Nav, NavItem, NavLink } from 'reactstrap';
import { getGateEvents, saveFlowConfig } from './api';

// Flow definitions (phase C): handover points as movement rules, locations, parties, control gates and the types.
// Nothing here is code: a new move (e.g. depot -> PDI) is a new point row, a new site a new location row.

const CHECKS = [['ENGEL', 'Engel'], ['UYARI', 'Uyarı'], ['KAPALI', 'Kapalı']];
const SIDES = [['KAYNAK', 'Kaynakta (çıkan → taşıyıcı)'], ['HEDEF', 'Hedefte (taşıyıcı → alan)'], ['DOGRUDAN', 'Doğrudan (çıkan → alan)']];
const FLOWS = [['HEPSI', 'Hepsi'], ['ITHALAT', 'İthalat'], ['YERLI', 'Yerli'], ['IHRACAT', 'İhracat']];
const ORDER_TYPES = [['WAREHOUSE_TRANSFER', 'Depo transfer'], ['PDI', 'PDI'], ['MANUAL_SHIPPING', 'Sevk (manuel)'],
    ['SHIPPING_BLOCKING', 'Sevk/blokaj'], ['INVOICE', 'Fatura']];
export const CHECK_COLOR = { ENGEL: 'danger', UYARI: 'warning', KAPALI: 'secondary' };

const Cell = ({ col, value, onChange }) => {
    switch (col.type) {
        case 'bool':
            return <Input type="checkbox" className="ml-0 position-static" checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
        case 'select':
            return (
                <Input bsSize="sm" type="select" value={value ?? ''} onChange={(e) => onChange(col.int ? Number(e.target.value) || 0 : e.target.value)}>
                    {col.empty !== undefined && <option value="">{col.empty}</option>}
                    {col.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Input>
            );
        case 'multi':
            return (
                <div className="af-multi">
                    {col.options.map(([v, l]) => (
                        <label key={v}><input type="checkbox" checked={(value || []).includes(v)}
                            onChange={(e) => onChange(e.target.checked ? [...(value || []), v] : (value || []).filter((x) => x !== v))} /> {l}</label>
                    ))}
                </div>
            );
        default:
            return <Input bsSize="sm" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
    }
};

// One editable row per record; only changed fields are sent, every save is logged on the server.
const EditTable = ({ table, rows, cols, keyOf, newKey, canEdit, reload, note }) => {
    const [drafts, setDrafts] = useState({});
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(null);
    const save = async (key, values) => {
        try {
            await saveFlowConfig(table, String(key), values);
            toast.success('Kaydedildi');
            setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
            setAdding(null);
            setEditing(null);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kaydedilemedi');
        }
    };
    return (
        <div>
            {note && <div className="dt-muted mb-2">{note}</div>}
            <div className="af-scroll">
                <table className="ct-table af-table">
                    <thead><tr>{newKey && <th>Kod</th>}{cols.map((c) => <th key={c.f}>{c.label}</th>)}{canEdit && <th></th>}</tr></thead>
                    <tbody>
                        {rows.map((r) => {
                            const k = keyOf(r);
                            const d = (editing === k && drafts[k]) || {};
                            return (
                                <tr key={k}>
                                    {newKey && <td><b>{k}</b></td>}
                                    {cols.map((c) => (
                                        <td key={c.f}>{editing === k && c.edit !== false && !(typeof c.edit === 'function' && !c.edit(r))
                                            ? <Cell col={c} value={c.f in d ? d[c.f] : r[c.f]} onChange={(v) => setDrafts({ ...drafts, [k]: { ...d, [c.f]: v } })} />
                                            : (c.show ? c.show(r) : String(r[c.f] ?? ''))}</td>
                                    ))}
                                    {canEdit && <td className="text-nowrap">{editing === k
                                        ? <>
                                            <Button size="sm" color="primary" className="mr-1" disabled={!Object.keys(d).length} onClick={() => save(k, d)}>Kaydet</Button>
                                            <Button size="sm" color="secondary" onClick={() => { setEditing(null); setDrafts({ ...drafts, [k]: undefined }); }}>Vazgeç</Button>
                                        </>
                                        : <Button size="sm" color="primary" outline onClick={() => setEditing(k)}>Düzenle</Button>}</td>}
                                </tr>
                            );
                        })}
                        {adding && (
                            <tr className="af-new">
                                {newKey && <td><Input bsSize="sm" placeholder="KOD" value={adding.__key || ''}
                                    onChange={(e) => setAdding({ ...adding, __key: e.target.value.toUpperCase() })} /></td>}
                                {cols.map((c) => <td key={c.f}>{c.edit !== false && <Cell col={c} value={adding[c.f]} onChange={(v) => setAdding({ ...adding, [c.f]: v })} />}</td>)}
                                <td><Button size="sm" color="primary" onClick={() => { const { __key, ...v } = adding; save(newKey ? __key : '0', v); }}>Ekle</Button></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {canEdit && !adding && <Button size="sm" color="primary" outline className="mt-2" onClick={() => setAdding({ is_active: true })}>+ Yeni</Button>}
        </div>
    );
};

// Two checks per gate: open damage that blocks shipment, and "handover required". Per-location overrides of either.
const GATE_CHECKS = [['damage_check', 'Açık hasar kontrolü'], ['handover_check', 'Devir-teslim zorunlu']];

const GateTable = ({ md, canEdit, reload }) => {
    const [add, setAdd] = useState({});
    const [pointsOf, setPointsOf] = useState(null); // gate whose accepted handovers are being edited
    const locName = (id) => md.locations.find((l) => l.id === id)?.name || id;
    const pointName = (c) => md.points.find((p) => p.code === c)?.name_tr || c;
    const save = (key, values) => saveFlowConfig(key.includes(':') ? 'control_gate_location' : 'control_gate', key, values)
        .then(() => { toast.success('Kaydedildi'); reload(); })
        .catch((e) => toast.error(e?.response?.data?.error || 'Kaydedilemedi'));
    const modeSelect = (value, onChange, inherit) => (
        <Input bsSize="sm" type="select" style={{ maxWidth: inherit ? 170 : 130 }} value={value || ''} onChange={(e) => onChange(e.target.value)}>
            {inherit && <option value="">{inherit}</option>}
            {CHECKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Input>
    );
    return (
        <div>
            <div className="dt-muted mb-2">Terminal okutmaları aracı hareket ettirmeden önce iki kontrol yapar. <b>Açık hasar:</b> hasar kodu ve durumu
                &quot;sevki engeller&quot; olan kapanmamış hasar. <b>Devir-teslim zorunlu:</b> bu hareketin devir-teslim kaydı (&quot;aranan devir-teslim&quot; noktalarından biri) (yalnızca tam custody istenen
                yerlerde açılır; iki uç aynı firmanın işletmesindeyse otomatik geçer). Engel: okutma reddedilir. Uyarı: geçer, istisna listesine yazılır.
                Kapalı: kontrol yok. Lokasyon bazında farklı ayar verilebilir.</div>
            <table className="ct-table af-table">
                <thead><tr><th>Kapı (okutma)</th>{GATE_CHECKS.map(([f, l]) => <th key={f}>{l}</th>)}<th>Aranan devir-teslim</th><th>Lokasyon bazında</th></tr></thead>
                <tbody>{md.gates.map((g) => {
                    const a = add[g.code] || {};
                    const setA = (v) => setAdd({ ...add, [g.code]: { ...a, ...v } });
                    return (
                        <tr key={g.code}>
                            <td><b>{g.name_tr}</b><div className="dt-muted">{g.code}</div></td>
                            {GATE_CHECKS.map(([f]) => (
                                <td key={f}>{canEdit
                                    ? modeSelect(g[f], (v) => save(g.code, { [f]: v }))
                                    : <Badge color={CHECK_COLOR[g[f]]}>{g[f]}</Badge>}</td>
                            ))}
                            <td>
                                {g.point_codes.map(pointName).join(' / ')}
                                {canEdit && <Button size="sm" color="link" className="p-0 ml-1" onClick={() => setPointsOf(pointsOf === g.code ? null : g.code)}>
                                    {pointsOf === g.code ? 'kapat' : 'değiştir'}</Button>}
                                {pointsOf === g.code && (
                                    <div className="af-multi mt-1">
                                        {md.points.filter((p) => p.is_active).map((p) => (
                                            <label key={p.code}><input type="checkbox" checked={g.point_codes.includes(p.code)}
                                                onChange={(e) => save(g.code, { point_codes: e.target.checked ? [...g.point_codes, p.code] : g.point_codes.filter((c) => c !== p.code) })} />
                                                {' '}{p.name_tr} <span className="dt-muted">({p.code})</span></label>
                                        ))}
                                    </div>
                                )}
                            </td>
                            <td>
                                {g.overrides.map((o) => (
                                    <div key={o.location_id} className="af-override">
                                        <b>{locName(o.location_id)}</b>
                                        {GATE_CHECKS.map(([f, l]) => (
                                            <div key={f} className="d-flex align-items-center">
                                                <span className="dt-muted mr-1">{l}:</span>
                                                {canEdit
                                                    ? modeSelect(o[f], (v) => save(`${g.code}:${o.location_id}`, { [f]: v }), `varsayılan (${g[f]})`)
                                                    : <Badge color={CHECK_COLOR[o[f] || g[f]]}>{o[f] || `varsayılan (${g[f]})`}</Badge>}
                                            </div>
                                        ))}
                                        {canEdit && <Button size="sm" color="link" className="p-0 text-danger"
                                            onClick={() => save(`${g.code}:${o.location_id}`, { damage_check: '', handover_check: '' })}>kaldır</Button>}
                                    </div>
                                ))}
                                {canEdit && (
                                    <div className="d-flex flex-wrap">
                                        <Input bsSize="sm" type="select" className="mr-1 mb-1" style={{ maxWidth: 180 }} value={a.loc || ''} onChange={(e) => setA({ loc: e.target.value })}>
                                            <option value="">lokasyon…</option>
                                            {md.locations.filter((l) => l.is_active && l.location_type !== 'BAYI' && !g.overrides.some((o) => o.location_id === l.id))
                                                .map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                        </Input>
                                        <Input bsSize="sm" type="select" className="mr-1 mb-1" style={{ maxWidth: 200 }} value={a.field || 'damage_check'} onChange={(e) => setA({ field: e.target.value })}>
                                            {GATE_CHECKS.map(([f, l]) => <option key={f} value={f}>{l}</option>)}
                                        </Input>
                                        <div className="mr-1 mb-1">{modeSelect(a.mode || 'KAPALI', (v) => setA({ mode: v }))}</div>
                                        <Button size="sm" color="primary" outline className="mb-1" disabled={!a.loc}
                                            onClick={() => save(`${g.code}:${a.loc}`, { [a.field || 'damage_check']: a.mode || 'KAPALI' })}>Ekle</Button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    );
                })}</tbody>
            </table>
        </div>
    );
};

export const GateEvents = () => {
    const [all, setAll] = useState(false);
    const [rows, setRows] = useState([]);
    useEffect(() => { getGateEvents(!all).then(setRows).catch(() => toast.error('Kapı kontrolleri alınamadı')); }, [all]);
    const color = { GECTI: 'success', AYNI_TARAF: 'info', UYARI: 'warning', ENGEL: 'danger', KAPALI: 'secondary' };
    const label = { GECTI: 'Geçti', AYNI_TARAF: 'Aynı taraf', UYARI: 'Uyarı', ENGEL: 'Engellendi', KAPALI: 'Kontrol kapalı' };
    return (
        <Card><CardBody>
            <div className="d-flex align-items-center mb-2">
                <span className="dt-muted mr-3">Son 30 gün. Terminal okutmalarında açık hasarla veya devir-teslim kaydı olmadan yapılan geçişler (uyarı) ve reddedilenler (engel).</span>
                <label className="mb-0"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> tüm kararları göster</label>
            </div>
            <table className="ct-table">
                <thead><tr><th>Zaman</th><th>Kapı</th><th>VIN</th><th>Lokasyon</th><th>Kontrol</th><th>Karar</th><th>Açıklama</th></tr></thead>
                <tbody>{rows.map((e) => (
                    <tr key={e.id}>
                        <td>{moment(e.created_at).format('DD.MM.YYYY HH:mm')}</td><td>{e.gate_name}</td><td><b>{e.chassis_no}</b></td>
                        <td>{e.location_name}</td><td>{e.check_kind === 'HASAR' ? 'Açık hasar' : 'Devir-teslim'}</td><td><Badge color={color[e.decision]}>{label[e.decision]}</Badge></td><td>{e.message}</td>
                    </tr>
                ))}</tbody>
            </table>
            {rows.length === 0 && <div className="dt-muted p-2">Kayıt yok.</div>}
        </CardBody></Card>
    );
};

const AkisTanimlari = ({ md, canEdit, reload }) => {
    const [sub, setSub] = useState('points');
    const [locType, setLocType] = useState('');
    const [q, setQ] = useState('');
    // dealers make the lists long: filter by type and by text (name, code, place, operator)
    const hit = (...xs) => !q || xs.some((x) => (x || '').toLocaleUpperCase('tr').includes(q.toLocaleUpperCase('tr')));
    const shownLocations = md.locations.filter((l) => (!locType || l.location_type === locType)
        && hit(l.name, l.shipping_code, l.place, l.operator_name, l.warehouse_name));
    const shownParties = md.parties.filter((p) => (!locType || p.party_types.includes(locType)) && hit(p.name));
    const filterBar = (types) => (
        <div className="d-flex mb-2" style={{ gap: 8, maxWidth: 560 }}>
            <Input bsSize="sm" type="select" value={locType} onChange={(e) => setLocType(e.target.value)}>
                <option value="">Tüm tipler</option>
                {types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Input>
            <Input bsSize="sm" placeholder="Ara (ad, kod, yer)" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
    );
    const locTypes = md.location_types.map((t) => [t.code, t.name_tr]);
    const partyTypes = md.party_types.map((t) => [t.code, t.name_tr]);
    const parties = md.parties.filter((p) => p.is_active).map((p) => [p.id, p.name]);
    const ports = md.locations.filter((l) => l.location_type === 'LIMAN').map((l) => [l.id, l.name]);
    const countries = (md.countries || []).map((c) => [c.code, `${c.code} – ${c.name_tr}`]);
    const warehouses = (md.warehouses || []).map((w) => [w.id, `${w.name} (${w.code})${w.is_active ? '' : ' – pasif'}`]);
    const ltName = (c) => md.location_types.find((t) => t.code === c)?.name_tr || 'herhangi';
    return (
        <Card><CardBody>
            <Nav pills className="mb-3">
                {[['points', 'Devir-teslim noktaları (hareket kuralları)'], ['locations', 'Lokasyonlar'], ['parties', 'Taraflar (firmalar)'],
                    ['gates', 'Kontrol kapıları'], ['types', 'Tipler']].map(([k, t]) => (
                    <NavItem key={k}><NavLink href="#" className={sub === k ? 'active' : ''} onClick={(e) => { e.preventDefault(); setSub(k); setLocType(''); setQ(''); }}>{t}</NavLink></NavItem>
                ))}
            </Nav>
            {sub === 'points' && (
                <EditTable table="handover_point" rows={md.points} keyOf={(r) => r.code} newKey canEdit={canEdit} reload={reload}
                    note="Her satır bir devir-teslim: hangi lokasyon tipinden hangisine, hangi uçta, hangi akışta, hangi emirle ve hangi kontrollerle. Taşıyıcı tarafı her taşıyıcı tipini kabul eder."
                    cols={[
                        { f: 'name_tr', label: 'Ad' },
                        { f: 'from_location_type', label: 'Çıkış tipi', type: 'select', options: locTypes, empty: 'herhangi', show: (r) => ltName(r.from_location_type) },
                        { f: 'to_location_type', label: 'Varış tipi', type: 'select', options: locTypes, empty: 'herhangi', show: (r) => ltName(r.to_location_type) },
                        { f: 'side', label: 'Uç', type: 'select', options: SIDES },
                        { f: 'flow', label: 'Akış', type: 'select', options: FLOWS },
                        { f: 'delivering_party_type', label: 'Teslim eden tipi', type: 'select', options: partyTypes, empty: '—' },
                        { f: 'receiving_party_type', label: 'Teslim alan tipi', type: 'select', options: partyTypes, empty: '—' },
                        { f: 'order_types', label: 'İzin veren emir', type: 'multi', options: ORDER_TYPES, show: (r) => r.order_types.join(', ') },
                        { f: 'check_order', label: 'Emir kontrolü', type: 'select', options: CHECKS },
                        { f: 'check_customs', label: 'Milli kontrolü', type: 'select', options: CHECKS },
                        { f: 'check_damage', label: 'Açık hasar', type: 'select', options: CHECKS },
                        { f: 'allow_two_step', label: 'İki adımlı', type: 'bool', show: (r) => (r.allow_two_step ? '✓' : '') },
                        { f: 'ends_chain', label: 'Zinciri bitirir', type: 'bool', show: (r) => (r.ends_chain ? '✓' : '') },
                        { f: 'writes_delivery', label: 'Teslimat (POD)', type: 'bool', show: (r) => (r.writes_delivery ? '✓' : '') },
                        { f: 'pod_document', label: 'Teslim belgesi', type: 'select', options: CHECKS },
                        { f: 'check_destination', label: 'Teslim yeri ≠ emir', type: 'select', options: CHECKS },
                        { f: 'check_location', label: 'Saha ≠ sistemdeki konum', type: 'select', options: CHECKS },
                        { f: 'is_active', label: 'Aktif', type: 'bool', show: (r) => (r.is_active ? '✓' : '') },
                    ]} />
            )}
            {sub === 'locations' && filterBar(locTypes)}
            {sub === 'locations' && (
                <EditTable table="location" rows={shownLocations} keyOf={(r) => r.id} canEdit={canEdit} reload={reload}
                    note="Depo tanımlamalarında açılan her yeni depo buraya otomatik eklenir (tip ve ülke sonradan düzeltilebilir). WMS deposu, aracın bulunduğu yeri ve kapı okutmalarının lokasyonunu belirler; bir depoda birden fazla alan (stok, PDI, grupaj) tanımlanabilir. İşletmeci, o lokasyonda varsayılan teslim eden / teslim alan taraftır. İhracat varış noktasında 'çıkış limanı', aracın gideceği limanı belirler. Sevkiyat Yerleri'ndeki her bayi teslim noktası buraya otomatik eklenir; sevk emrinin bayi kodu varışı belirler."
                    cols={[
                        { f: 'name', label: 'Ad' },
                        { f: 'location_type', label: 'Tip', type: 'select', options: locTypes },
                        { f: 'country', label: 'Ülke', type: 'select', options: countries, edit: (r) => !r.country_from_address,
                            show: (r) => (r.country_from_address ? `${r.country} (adresten)` : r.country) },
                        { f: 'operator_party_id', label: 'İşletmeci', type: 'select', int: true, options: parties, empty: '— (listeden seçilir)', show: (r) => r.operator_name || '—' },
                        { f: 'place', label: 'Yer (adres)', edit: false },
                        { f: 'warehouse_id', label: 'Depo (WMS)', type: 'select', int: true, options: warehouses, empty: '—', show: (r) => r.warehouse_name || '—' },
                        { f: 'via_location_id', label: 'Çıkış limanı (ihracat)', type: 'select', int: true, options: ports, empty: '—',
                            show: (r) => md.locations.find((l) => l.id === r.via_location_id)?.name || '' },
                        { f: 'shipping_code', label: 'Sipariş kodu', edit: false },
                        { f: 'is_active', label: 'Aktif', type: 'bool', show: (r) => (r.is_active ? '✓' : '') },
                    ]} />
            )}
            {sub === 'parties' && (
                <div className="p-2">Firmalar (operatörler, taşıyıcılar, limanlar, fabrikalar) artık <a href="/tanimlamalar/firmalar"><b>Tanımlamalar &gt; Firmalar</b></a> ekranında
                    tanımlanır; saha operatörleri <a href="/depo-yonetimi/saha-operatorler">Depo Yönetimi &gt; Saha ve Operatörler</a>, kullanıcının firması Kullanıcı Yönetimi'nde seçilir.</div>
            )}
            {sub === 'gates' && <GateTable md={md} canEdit={canEdit} reload={reload} />}
            {sub === 'types' && (
                <div>
                    <h6>Taraf tipleri</h6>
                    <EditTable table="party_type" rows={md.party_types} keyOf={(r) => r.code} newKey canEdit={canEdit} reload={reload}
                        cols={[{ f: 'name_tr', label: 'Ad (TR)' }, { f: 'name_en', label: 'Ad (EN)' },
                            { f: 'is_carrier', label: 'Taşıyıcı', type: 'bool', show: (r) => (r.is_carrier ? '✓' : '') },
                            { f: 'is_active', label: 'Aktif', type: 'bool', show: (r) => (r.is_active ? '✓' : '') }]} />
                    <h6 className="mt-4">Lokasyon tipleri</h6>
                    <EditTable table="location_type" rows={md.location_types} keyOf={(r) => r.code} newKey canEdit={canEdit} reload={reload}
                        cols={[{ f: 'name_tr', label: 'Ad (TR)' }, { f: 'name_en', label: 'Ad (EN)' },
                            { f: 'is_active', label: 'Aktif', type: 'bool', show: (r) => (r.is_active ? '✓' : '') }]} />
                </div>
            )}
        </CardBody></Card>
    );
};

export default AkisTanimlari;
